import Vue from "vue";
import raftRestClient from "../../lib/raft_rest_client";
import { restClient } from "../../lib/raftApi";
import { useDrupalSettings } from "../../composables/useDrupalSettings.js";

const { config } = useDrupalSettings();

const state = {
  serviceTypes: [],
  requirementStatuses: [],
  displayOptions: [],
  selectedServiceTypes: [],
  selectedRequirementStatuses: [],
  selectedDepartments: [],
  selectedGroups: [],
  selectedSmallAnimals: [],
  selectedLargeAnimals: [],
  selectedWildAnimals: [],
  selectedNhpAnimals: [],
  selectedCriteria: {},
  selectedSatisfiers: {},
  medSurRecordWorkflowStates: [
    { value: null, text: "-- Select --" },
    { value: "approved", text: "Approved" },
    { value: "awaiting_review", text: "Awaiting Review" },
    { value: "denied", text: "Denied" },
    { value: "expired", text: "Expired" },
    { value: "follow_up_required", text: "Follow-Up Required" },
    { value: "completed", text: "Completed" },
    { value: "not_completed", text: "Not Completed" },
  ],
  medSurRecordWorkflowOptions: {
    completed: [
      { value: "not_completed", text: "Not Completed" },
      { value: "expired", text: "Expired" },
    ],
    not_completed: [
      { value: "completed", text: "Completed" },
      { value: "expired", text: "Expired" },
      { value: "denied", text: "Denied" },
      { value: "approved", text: "Approved" },
      { value: "awaiting_review", text: "Awaiting Review" },
    ],
    approved: [
      { value: "expired", text: "Expired" },
      { value: "awaiting_review", text: "Awaiting Review" },
    ],
    awaiting_review: [
      { value: "expired", text: "Expired" },
      { value: "denied", text: "Denied" },
      { value: "approved", text: "Approved" },
    ],
    denied: [
      { value: "expired", text: "Expired" },
      { value: "awaiting_review", text: "Awaiting Review" },
    ],
    expired: [
      { value: "not_completed", text: "Not Completed" },
      { value: "completed", text: "Completed" },
    ],
    follow_up_required: [
      { value: "expired", text: "Expired" },
      { value: "denied", text: "Denied" },
      { value: "awaiting_review", text: "Awaiting Review" },
    ],
  },
  requirementStateOptions: [
    { value: "open", text: "Not Completed" },
    // {value: 'completed', text: 'Completed'},
    // {value: 'not_completed', text: 'Not Completed'},
    { value: "delinquent", text: "Delinquent" },
    // {value: 'canceled', text: 'Canceled'},
    // {value: 'exempt', text: 'Exempt'},
  ],
  selectedRequirementStateOptions: [],
  selectedWorkflowStates: [],
  selectedRelatedStates: [],
  nextDueBetweenStart: "",
  nextDueBetweenEnd: "",
  recordDateBetweenStart: "",
  recordDateBetweenEnd: "",
  userTextFilter: "",
  requirementListingFilters: {},
  requirementListingFilterUpdate: "",
  recordFileAttachments: [],
  medicalSurveillancePermissionList: [
    "edit terms in medical_surveillance_service_types",
    "edit any medical_surveillance_record content",
    "view own medical_surveillance_record content",
    "view any medical_surveillance_record content",
    "edit any medical_surveillance_record content",
    "create medical_surveillance_record content",
    "view requirement_instances listings",
    "edit requirement rule entities",
    "create requirement_rule_medical_surveillance entities",
    "edit any requirement_rule_medical_surveillance entities",
    "edit own requirement_rule_medical_surveillance entities",
    "view published requirement rule entities",
    "export requirement instances",
    "export medical surveillance records",
    "force requirement instance renewal",
    "can inspect requirement_rule_medical_surveillance entities",
    "view medical surveillance records in group",
    "view medical surveillance requirements in group",
  ],
  userPermissions: [],
  schedulerOptions: {},
  dateOffset: {},
  smallAnimalsOptions: [],
  largeAnimalsOptions: [],
  wildAnimalsOptions: [],
  nhpAnimalsOptions: [],
  requirementRuleSummary: "",
  requirementOverrides: [],
  requirementRuleId: null,
  mainServiceType: "",
  animalSpeciesMap: {},
  animalSpeciesLabels: {},
};

const mutations = {
  setServiceTypes: (state, types) => (state.serviceTypes = types),
  setSelectedServiceTypes: (state, types) =>
    (state.selectedServiceTypes = types),
  setSelectedRequirementStatuses: (state, statuses) =>
    (state.selectedRequirementStatuses = statuses),
  setNextDueBetweenStart: (state, date) => (state.nextDueBetweenStart = date),
  setNextDueBetweenEnd: (state, date) => (state.nextDueBetweenEnd = date),
  setRecordBetweenStart: (state, date) => (state.recordDateBetweenStart = date),
  setRecordBetweenEnd: (state, date) => (state.recordDateBetweenEnd = date),
  setUserTextFilter: (state, user) => (state.userTextFilter = user),
  setRecordFileAttachments: (state, files) =>
    (state.recordFileAttachments = files),
  setMedSurRecordWorkflowStates: (state, states) =>
    (state.medSurRecordWorkflowStates = states),
  setRequirementListingFilters: (state, filters) =>
    (state.requirementListingFilters = filters),
  setRequirementListingFilterUpdate: (state, filters) =>
    (state.requirementListingFilterUpdate = filters),
  setUserPermissions: (state, perms) => (state.userPermissions = perms),
  setSelectedCriteria: (state, criteria) => (state.selectedCriteria = criteria),
  setSelectedSatisfiers: (state, satisfiers) =>
    (state.selectedSatisfiers = satisfiers),
  setSchedulerOptions: (state, scheduler_options) =>
    (state.schedulerOptions = scheduler_options),
  setDateOffset: (state, date_offset) => (state.dateOffset = date_offset),
  setRequirementRuleSummary: (state, content) =>
    (state.requirementRuleSummary = content),
  setRequirementOverrides: (state, overrides) =>
    (state.requirementOverrides = overrides),
  setRequirementRuleId: (state, id) => (state.requirementRuleId = id),
  setSelectedWorkflowStates: (state, states) =>
    (state.selectedWorkflowStates = states),
  setSelectedRelatedStates: (state, states) =>
    (state.selectedRelatedStates = states),
  setSelectedRequirementStateOptions: (state, states) =>
    (state.selectedRequirementStateOptions = states),
  setMainServiceType: (state, type) => (state.mainServiceType = type),
  setSelectedSmallAnimals: (state, type) => (state.selectedSmallAnimals = type),
  setSelectedLargeAnimals: (state, type) => (state.selectedLargeAnimals = type),
  setSelectedWildAnimals: (state, type) => (state.selectedWildAnimals = type),
  setSelectedNhpAnimals: (state, type) => (state.selectedNhpAnimals = type),
  setSmallAnimalsOptions: (state, type) => (state.smallAnimalsOptions = type),
  setLargeAnimalsOptions: (state, type) => (state.largeAnimalsOptions = type),
  setWildAnimalsOptions: (state, type) => (state.wildAnimalsOptions = type),
  setNhpAnimalsOptions: (state, type) => (state.nhpAnimalsOptions = type),
  setSelectedDepartments: (state, type) => (state.selectedDepartments = type),
  setSelectedGroups: (state, type) => (state.selectedGroups = type),
  setAnimalSpeciesMap: (state, type) => (state.animalSpeciesMap = type),
  setAnimalSpeciesLabels: (state, type) => (state.animalSpeciesLabels = type),
};
const getters = {
  getServiceTypes: (state) => state.serviceTypes,
  getSelectedServiceTypes: (state) => state.selectedServiceTypes,
  getRecordFileAttachments: (state) => state.recordFileAttachments,
  getMedSurRecordWorkflowStates: (state) => state.medSurRecordWorkflowStates,
  getUserPermissions: (state) => state.userPermissions,
  getMedSurRecordWorkflowOptions: (state) => state.medSurRecordWorkflowOptions,
  getRequirementRuleSummary: (state) => state.requirementRuleSummary,
  getMedSurListingFilterUpdate: (state) => state.requirementListingFilterUpdate,
  getMedSurListingFilters: (state) => state.requirementListingFilters,
  getSelectedWorkflowStates: (state) => state.selectedWorkflowStates,
  getSelectedRelatedStates: (state) => state.selectedRelatedStates,
  getMainServiceType: (state) => state.mainServiceType,
  getSelectedTypeThreeAnimals: (state) => state.selectedSmallAnimals,
  getSelectedTypeTwoAnimals: (state) => state.selectedLargeAnimals,
  getSelectedTypeOneAnimals: (state) => state.selectedWildAnimals,
  getSelectedTypeFourAnimals: (state) => state.selectedNhpAnimals,
  getTypeThreeAnimalsOptions: (state) => state.smallAnimalsOptions,
  getAnimalsTypeTwoOptions: (state) => state.largeAnimalsOptions,
  getTypeOneAnimalsOptions: (state) => state.wildAnimalsOptions,
  getTypeFourAnimalsOptions: (state) => state.nhpAnimalsOptions,
  getAnimalSpeciesMap: (state) => state.animalSpeciesMap,
  getAnimalSpeciesLabels: (state) => state.animalSpeciesLabels,
  getSelectedGroups: (state) => state.selectedGroups,
  getSelectedDepartments: (state) => state.selectedDepartments,
};

const actions = {
  fetchServiceTypes({ state, commit, rootState }) {
    const query = {};
    // Set group context.  Note: groups are always nodes in B4.
    if (config.groupContext) {
      query["rog-id"] = config.groupContext;
      query["rog-type"] = "node";
    }
    raftRestClient.getRest(
      rootState.config.restUrl + "/medical-surveillance/service-types",
      query,
      (data) => {
        const serviceTypes = [];
        for (const [key, value] of Object.entries(data.service_types)) {
          serviceTypes.push({
            id: value.id,
            title: value.label,
            uuid: value.uuid,
          });
        }

        commit("setServiceTypes", serviceTypes);
      },
      null,
      this,
      true,
    );
  },
  async fetchAnimalsSpecies({ state, commit, rootState }) {
    const data = await restClient.get({
      path: "medical-surveillance/species",
      queries: {},
    });
    const largeAnimals = [];
    const smallAnimals = [];
    const wildAnimals = [];
    const nhpAnimals = [];
    const speciesMap = {};
    if (data) {
      Object.entries(data.animals_large).forEach((animal) => {
        speciesMap[animal[0]] = animal[1];
        largeAnimals.push({ value: animal[0], text: animal[1] });
      });
      Object.entries(data.animals_small).forEach((animal) => {
        speciesMap[animal[0]] = animal[1];
        smallAnimals.push({ value: animal[0], text: animal[1] });
      });
      Object.entries(data.animals_wild).forEach((animal) => {
        speciesMap[animal[0]] = animal[1];
        wildAnimals.push({ value: animal[0], text: animal[1] });
      });
      Object.entries(data.animals_nhp).forEach((animal) => {
        speciesMap[animal[0]] = animal[1];
        nhpAnimals.push({ value: animal[0], text: animal[1] });
      });
    }
    commit("setSmallAnimalsOptions", smallAnimals);
    commit("setLargeAnimalsOptions", largeAnimals);
    commit("setWildAnimalsOptions", wildAnimals);
    commit("setNhpAnimalsOptions", nhpAnimals);
    commit("setAnimalSpeciesMap", speciesMap);
    commit("setAnimalSpeciesLabels", data.labels);
  },
  clearFilters({ commit }) {
    commit("setSelectedServiceTypes", []);
    commit("setSelectedRequirementStatuses", []);
    commit("setNextDueBetweenStart", "");
    commit("setNextDueBetweenEnd", "");
    commit("setUserTextFilter", "");
  },
  updateSelectedServiceTypes({ commit, state }, value) {
    commit("setSelectedServiceTypes", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
  },
  updateSelectedRequirementStatuses({ commit }, value) {
    commit("setSelectedRequirementStatuses", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
  },
  updateSelectedWorkflowStates({ commit }, value) {
    commit("setSelectedWorkflowStates", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
    const states = value.map((item) => item.value);
    if (states) {
      Vue.set(state.requirementListingFilters, "record_state", states);
    } else {
      Vue.delete(state.requirementListingFilters, "record_state");
    }
  },
  updateSelectedRelatedStates({ commit }, value) {
    commit("setSelectedRelatedStates", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
    const states = value.map((item) => item.value);
    if (states) {
      Vue.set(
        state.requirementListingFilters,
        "field_med_surv_record_status_value",
        states,
      );
    } else {
      Vue.delete(
        state.requirementListingFilters,
        "field_med_surv_record_status_value",
      );
    }
  },
  updateSelectedRequirementStateOptions({ commit }, value) {
    commit("setSelectedRequirementStateOptions", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
    const states = value.map((item) => item.value);
    if (states) {
      Vue.set(state.requirementListingFilters, "state", states);
    } else {
      Vue.delete(state.requirementListingFilters, "state");
    }
  },
  updateNextDueBetweenEnd({ commit, state }, value) {
    commit("setNextDueBetweenEnd", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
    let date = new Date(value);
    if (value) {
      date =
        date.getFullYear() +
        "-" +
        (date.getMonth() + 1) +
        "-" +
        date.getDate() +
        " 24:59:59";
      Vue.set(state.requirementListingFilters, "due_date_end", date);
    } else {
      Vue.delete(state.requirementListingFilters, "due_date_end");
    }
  },
  updateNextDueBetweenStart({ commit, state }, value) {
    commit("setNextDueBetweenStart", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
    let date = new Date(value);
    if (value) {
      date =
        date.getFullYear() +
        "-" +
        (date.getMonth() + 1) +
        "-" +
        date.getDate() +
        " 00:00:00";
      Vue.set(state.requirementListingFilters, "due_date_start", date);
    } else {
      Vue.delete(state.requirementListingFilters, "due_date_start");
    }
  },
  updateRecordDateBetweenStart({ commit, state }, value) {
    commit("setRecordBetweenEnd", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
    let date = new Date(value);
    if (value) {
      date =
        date.getFullYear() +
        "-" +
        (date.getMonth() + 1) +
        "-" +
        date.getDate() +
        " 00:00:00";
      Vue.set(
        state.requirementListingFilters,
        "field_med_surv_record_date_start",
        date,
      );
    } else {
      Vue.delete(
        state.requirementListingFilters,
        "field_med_surv_record_date_start",
      );
    }
  },
  updateRecordDateBetweenEnd({ commit, state }, value) {
    commit("setRecordBetweenEnd", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
    let date = new Date(value);
    if (value) {
      date =
        date.getFullYear() +
        "-" +
        (date.getMonth() + 1) +
        "-" +
        date.getDate() +
        " 23:59:59";
      Vue.set(
        state.requirementListingFilters,
        "field_med_surv_record_date_end",
        date,
      );
    } else {
      Vue.delete(
        state.requirementListingFilters,
        "field_med_surv_record_date_end",
      );
    }
  },
  updateRecordFileAttachments({ commit }, value) {
    commit("setRecordFileAttachments", value);
  },
  updateUserTextFilter({ commit, state }, value) {
    commit("setUserTextFilter", value);
    if (value.length >= 2) {
      Vue.set(
        state.requirementListingFilters,
        "field_prof_display_name_value",
        value,
      );
    } else {
      Vue.delete(
        state.requirementListingFilters,
        "field_prof_display_name_value",
      );
    }
    // Trigger table filtering for anything except a 1 character entry.
    if (value.length !== 1) {
      commit("setRequirementListingFilterUpdate", new Date().getTime());
    }
  },
  fetchMedicalSurveillanceUserPermissions({ state, commit, rootState }) {
    const query = {
      permissions: state.medicalSurveillancePermissionList,
    };

    // Set group context.  Note: groups are always nodes in B4.
    if (config.groupContext) {
      query["rog-id"] = config.groupContext;
      query["rog-type"] = "node";
    }

    raftRestClient.getRest(
      rootState.config.restUrl + "/perms",
      query,
      (data) => {
        const userPerms = [];
        for (const [key, value] of Object.entries(data.permissions)) {
          if (value) {
            userPerms.push(key);
          }
        }
        commit("setUserPermissions", userPerms);
      },
      null,
      this,
      true,
    );
  },
  updateSelectedCriteria({ commit }, value) {
    commit("setSelectedCriteria", value);
  },
  updateSelectedSatisfiers({ commit }, value) {
    commit("setSelectedSatisfiers", value);
  },
  updateSchedulerOptions({ commit }, value) {
    commit("setSchedulerOptions", value);
  },
  updateDateOffset({ commit }, value) {
    commit("setDateOffset", value);
  },
  updateRequirementRuleSummary({ commit }, value) {
    commit("setRequirementRuleSummary", value);
  },
  updateRequirementOverrides({ commit }, value) {
    commit("setRequirementOverrides", value);
  },
  updateRequirementRuleId({ commit }, value) {
    commit("setRequirementRuleId", value);
  },
  updateMainServiceType({ commit }, value) {
    commit("setMainServiceType", value);
  },
  updateSelectedTypeThreeAnimals({ commit }, value) {
    commit("setSelectedSmallAnimals", value);
  },
  updateSelectedTypeTwoAnimals({ commit }, value) {
    commit("setSelectedLargeAnimals", value);
  },
  updateSelectedTypeOneAnimals({ commit }, value) {
    commit("setSelectedWildAnimals", value);
  },
  updateSelectedTypeFourAnimals({ commit }, value) {
    commit("setSelectedNhpAnimals", value);
  },
  updateSelectedDepartments({ commit }, value) {
    commit("setSelectedDepartments", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
  },
  updateSelectedGroups({ commit }, value) {
    commit("setSelectedGroups", value);
    commit("setRequirementListingFilterUpdate", new Date().getTime());
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
